import AdminLayout from "@/components/layout-admin";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Globe, DollarSign, Copy, ExternalLink, AlertTriangle } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { insertBookingPageSchema, insertRatePlanSchema, type BookingPage, type RatePlan, type Apartment } from "@shared/schema";
import { api, buildUrl } from "@shared/routes";
import { z } from "zod";
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";

const bookingPageFormSchema = insertBookingPageSchema.extend({
  title: z.string().min(1, "Title is required"),
  slug: z.string().min(1, "Slug is required"),
});

type BookingPageFormValues = z.infer<typeof bookingPageFormSchema>;

const ratePlanFormSchema = insertRatePlanSchema.extend({
  name: z.string().min(1, "Name is required"),
  apartmentId: z.coerce.number().min(1, "Select an apartment"),
  baseNightlyRate: z.string().min(1, "Base rate is required"),
});

type RatePlanFormValues = z.infer<typeof ratePlanFormSchema>;

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

function BookingPageForm({
  defaultValues,
  onSubmit,
  isPending,
  submitLabel,
}: {
  defaultValues: BookingPageFormValues;
  onSubmit: (values: BookingPageFormValues) => void;
  isPending: boolean;
  submitLabel: string;
}) {
  const form = useForm<BookingPageFormValues>({
    resolver: zodResolver(bookingPageFormSchema),
    defaultValues,
  });

  useEffect(() => {
    form.reset(defaultValues);
  }, [defaultValues]);

  const titleValue = form.watch("title");

  useEffect(() => {
    if (titleValue && !form.getValues("slug")) {
      form.setValue("slug", slugify(titleValue));
    }
  }, [titleValue]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Title</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    data-testid="input-booking-page-title"
                    onChange={(e) => {
                      field.onChange(e);
                      const currentSlug = form.getValues("slug");
                      if (!currentSlug || currentSlug === slugify(field.value || "")) {
                        form.setValue("slug", slugify(e.target.value));
                      }
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="slug"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Slug</FormLabel>
                <FormControl><Input {...field} data-testid="input-booking-page-slug" /></FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl><Textarea {...field} value={field.value || ""} data-testid="input-booking-page-description" /></FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="isActive"
          render={({ field }) => (
            <FormItem className="flex items-center gap-3">
              <FormControl>
                <Switch
                  checked={field.value ?? true}
                  onCheckedChange={field.onChange}
                  data-testid="switch-booking-page-active"
                />
              </FormControl>
              <FormLabel className="!mt-0">Active</FormLabel>
            </FormItem>
          )}
        />

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <FormField
            control={form.control}
            name="minStay"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Min Stay (nights)</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    {...field}
                    value={field.value ?? 1}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                    data-testid="input-booking-page-min-stay"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="maxStay"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Max Stay (nights)</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    {...field}
                    value={field.value ?? 365}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                    data-testid="input-booking-page-max-stay"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="advanceBookingDays"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Advance Booking Days</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    {...field}
                    value={field.value ?? 365}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                    data-testid="input-booking-page-advance-days"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="primaryColor"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Primary Color</FormLabel>
              <FormControl>
                <div className="flex gap-2 items-center">
                  <Input {...field} value={field.value || "#C2652A"} data-testid="input-booking-page-primary-color" />
                  <div
                    className="h-9 w-9 rounded-md border flex-shrink-0"
                    style={{ backgroundColor: field.value || "#C2652A" }}
                  />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="termsAndConditions"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Terms & Conditions (optional)</FormLabel>
              <FormControl><Textarea {...field} value={field.value || ""} data-testid="input-booking-page-terms" /></FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="cancellationPolicy"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Cancellation Policy (optional)</FormLabel>
              <FormControl><Textarea {...field} value={field.value || ""} data-testid="input-booking-page-cancellation" /></FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button type="submit" className="w-full" disabled={isPending} data-testid="button-submit-booking-page">
          {isPending ? "Saving..." : submitLabel}
        </Button>
      </form>
    </Form>
  );
}

function RatePlanForm({
  defaultValues,
  onSubmit,
  isPending,
  submitLabel,
  apartments,
}: {
  defaultValues: RatePlanFormValues;
  onSubmit: (values: RatePlanFormValues) => void;
  isPending: boolean;
  submitLabel: string;
  apartments: Apartment[];
}) {
  const form = useForm<RatePlanFormValues>({
    resolver: zodResolver(ratePlanFormSchema),
    defaultValues,
  });

  useEffect(() => {
    form.reset(defaultValues);
  }, [defaultValues]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="apartmentId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Apartment</FormLabel>
              <Select onValueChange={field.onChange} value={field.value?.toString()}>
                <FormControl>
                  <SelectTrigger data-testid="select-rate-plan-apartment">
                    <SelectValue placeholder="Select apartment" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {apartments.map((a) => (
                    <SelectItem key={a.id} value={a.id.toString()}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Plan Name</FormLabel>
              <FormControl><Input {...field} data-testid="input-rate-plan-name" /></FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <FormField
            control={form.control}
            name="baseNightlyRate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Base Nightly Rate</FormLabel>
                <FormControl><Input {...field} data-testid="input-rate-plan-base-rate" /></FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="cleaningFee"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Cleaning Fee</FormLabel>
                <FormControl><Input {...field} value={field.value || "0"} data-testid="input-rate-plan-cleaning-fee" /></FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="weekendRate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Weekend Rate</FormLabel>
                <FormControl><Input {...field} value={field.value || ""} placeholder="Same as base" data-testid="input-rate-plan-weekend-rate" /></FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="weeklyDiscount"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Weekly Discount %</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    {...field}
                    value={field.value ?? 0}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                    data-testid="input-rate-plan-weekly-discount"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="monthlyDiscount"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Monthly Discount %</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    {...field}
                    value={field.value ?? 0}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                    data-testid="input-rate-plan-monthly-discount"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="taxRate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Tax Rate %</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    value={field.value ?? "0"}
                    placeholder="0"
                    data-testid="input-rate-plan-tax-rate"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="taxName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Tax Label</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    value={field.value ?? "Tax"}
                    placeholder="Tax"
                    data-testid="input-rate-plan-tax-name"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="isActive"
          render={({ field }) => (
            <FormItem className="flex items-center gap-3">
              <FormControl>
                <Switch
                  checked={field.value ?? true}
                  onCheckedChange={field.onChange}
                  data-testid="switch-rate-plan-active"
                />
              </FormControl>
              <FormLabel className="!mt-0">Active</FormLabel>
            </FormItem>
          )}
        />

        <Button type="submit" className="w-full" disabled={isPending} data-testid="button-submit-rate-plan">
          {isPending ? "Saving..." : submitLabel}
        </Button>
      </form>
    </Form>
  );
}

function RatePlansDialog({
  bookingPage,
  apartments,
}: {
  bookingPage: BookingPage;
  apartments: Apartment[];
}) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [editRatePlan, setEditRatePlan] = useState<RatePlan | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createPending, setCreatePending] = useState(false);
  const [updatePending, setUpdatePending] = useState(false);
  const [deletePending, setDeletePending] = useState(false);

  const { data: ratePlans, isLoading } = useQuery<RatePlan[]>({
    queryKey: [api.booking.pages.ratePlans.path.replace(":id", String(bookingPage.id))],
    enabled: open,
  });

  const createDefaults: RatePlanFormValues = {
    organizationId: bookingPage.organizationId,
    apartmentId: 0,
    bookingPageId: bookingPage.id,
    name: "",
    baseNightlyRate: "100.00",
    cleaningFee: "0",
    weekendRate: null,
    weeklyDiscount: 0,
    monthlyDiscount: 0,
    currency: "USD",
    taxRate: "0",
    taxName: "Tax",
    isActive: true,
  };

  async function onCreateRatePlan(values: RatePlanFormValues) {
    setCreatePending(true);
    try {
      await apiRequest("POST", api.booking.ratePlans.create.path, {
        ...values,
        organizationId: bookingPage.organizationId,
        bookingPageId: bookingPage.id,
      });
      toast({ title: "Rate plan created" });
      setShowCreateForm(false);
      queryClient.invalidateQueries({ queryKey: [api.booking.pages.ratePlans.path.replace(":id", String(bookingPage.id))] });
    } catch {
      toast({ title: "Error", description: "Failed to create rate plan", variant: "destructive" });
    } finally {
      setCreatePending(false);
    }
  }

  async function onEditRatePlan(values: RatePlanFormValues) {
    if (!editRatePlan) return;
    setUpdatePending(true);
    try {
      await apiRequest("PUT", buildUrl(api.booking.ratePlans.update.path, { id: editRatePlan.id }), values);
      toast({ title: "Rate plan updated" });
      setEditRatePlan(null);
      queryClient.invalidateQueries({ queryKey: [api.booking.pages.ratePlans.path.replace(":id", String(bookingPage.id))] });
    } catch {
      toast({ title: "Error", description: "Failed to update rate plan", variant: "destructive" });
    } finally {
      setUpdatePending(false);
    }
  }

  async function handleDeleteRatePlan(id: number) {
    setDeletePending(true);
    try {
      await apiRequest("DELETE", buildUrl(api.booking.ratePlans.delete.path, { id }));
      toast({ title: "Rate plan deleted" });
      queryClient.invalidateQueries({ queryKey: [api.booking.pages.ratePlans.path.replace(":id", String(bookingPage.id))] });
    } catch {
      toast({ title: "Error", description: "Failed to delete rate plan", variant: "destructive" });
    } finally {
      setDeletePending(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setShowCreateForm(false); setEditRatePlan(null); } }}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2" data-testid={`button-rate-plans-${bookingPage.id}`}>
          <DollarSign className="w-4 h-4" />
          Rate Plans
          {!isLoading && ratePlans?.filter(rp => rp.isActive).length === 0 && (
            <AlertTriangle className="w-3.5 h-3.5 text-amber-500" aria-label="No active rate plans" />
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Rate Plans - {bookingPage.title}</DialogTitle>
        </DialogHeader>

        {!showCreateForm && !editRatePlan && (
          <div className="space-y-4">
            <div className="flex justify-end">
              <Button onClick={() => setShowCreateForm(true)} data-testid="button-add-rate-plan">
                <Plus className="w-4 h-4 mr-2" /> Add Rate Plan
              </Button>
            </div>

            {isLoading && <div className="text-center py-6 text-muted-foreground">Loading rate plans...</div>}

            {ratePlans && ratePlans.length === 0 && (
              <div className="text-center py-6 text-muted-foreground">No rate plans yet. Add one to get started.</div>
            )}

            <div className="space-y-3">
              {ratePlans?.map((rp) => (
                <Card key={rp.id} data-testid={`card-rate-plan-${rp.id}`}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold" data-testid={`text-rate-plan-name-${rp.id}`}>{rp.name}</span>
                          <Badge variant={rp.isActive ? "default" : "secondary"} data-testid={`badge-rate-plan-status-${rp.id}`}>
                            {rp.isActive ? "Active" : "Inactive"}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1" data-testid={`text-rate-plan-apartment-${rp.id}`}>
                          {apartments.find((a) => a.id === rp.apartmentId)?.name || "Unknown Apartment"}
                        </p>
                        <div className="flex gap-4 mt-2 text-sm flex-wrap">
                          <span>Base: ${rp.baseNightlyRate}/night</span>
                          {rp.cleaningFee && Number(rp.cleaningFee) > 0 && <span>Cleaning: ${rp.cleaningFee}</span>}
                          {rp.weekendRate && <span>Weekend: ${rp.weekendRate}/night</span>}
                          {rp.weeklyDiscount && rp.weeklyDiscount > 0 && <span>Weekly: -{rp.weeklyDiscount}%</span>}
                          {rp.monthlyDiscount && rp.monthlyDiscount > 0 && <span>Monthly: -{rp.monthlyDiscount}%</span>}
                          {rp.taxRate && Number(rp.taxRate) > 0 && <span>{rp.taxName || "Tax"}: {rp.taxRate}%</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => setEditRatePlan(rp)}
                          data-testid={`button-edit-rate-plan-${rp.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="icon" variant="ghost" data-testid={`button-delete-rate-plan-${rp.id}`}>
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Rate Plan</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete "{rp.name}"? This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel data-testid="button-cancel-delete-rate-plan">Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDeleteRatePlan(rp.id)} data-testid="button-confirm-delete-rate-plan">
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {showCreateForm && (
          <div>
            <div className="flex items-center justify-between mb-4 gap-2">
              <h3 className="font-semibold">New Rate Plan</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowCreateForm(false)} data-testid="button-cancel-create-rate-plan">
                Cancel
              </Button>
            </div>
            <RatePlanForm
              defaultValues={createDefaults}
              onSubmit={onCreateRatePlan}
              isPending={createPending}
              submitLabel="Create Rate Plan"
              apartments={apartments}
            />
          </div>
        )}

        {editRatePlan && (
          <div>
            <div className="flex items-center justify-between mb-4 gap-2">
              <h3 className="font-semibold">Edit Rate Plan</h3>
              <Button variant="ghost" size="sm" onClick={() => setEditRatePlan(null)} data-testid="button-cancel-edit-rate-plan">
                Cancel
              </Button>
            </div>
            <RatePlanForm
              defaultValues={{
                organizationId: editRatePlan.organizationId,
                apartmentId: editRatePlan.apartmentId,
                bookingPageId: editRatePlan.bookingPageId,
                name: editRatePlan.name,
                baseNightlyRate: editRatePlan.baseNightlyRate,
                cleaningFee: editRatePlan.cleaningFee || "0",
                weekendRate: editRatePlan.weekendRate || null,
                weeklyDiscount: editRatePlan.weeklyDiscount ?? 0,
                monthlyDiscount: editRatePlan.monthlyDiscount ?? 0,
                currency: editRatePlan.currency,
                taxRate: editRatePlan.taxRate ?? "0",
                taxName: editRatePlan.taxName ?? "Tax",
                isActive: editRatePlan.isActive,
              }}
              onSubmit={onEditRatePlan}
              isPending={updatePending}
              submitLabel="Update Rate Plan"
              apartments={apartments}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function AdminBooking() {
  const { toast } = useToast();
  const { user } = useAuth();
  const [createOpen, setCreateOpen] = useState(false);
  const [editPage, setEditPage] = useState<BookingPage | null>(null);
  const [createPending, setCreatePending] = useState(false);
  const [updatePending, setUpdatePending] = useState(false);
  const [deletePending, setDeletePending] = useState(false);

  const { data: bookingPages, isLoading } = useQuery<BookingPage[]>({
    queryKey: [api.booking.pages.list.path],
  });

  const { data: apartments } = useQuery<Apartment[]>({
    queryKey: [api.apartments.list.path],
  });

  const { data: orgData } = useQuery<{ slug: string }>({
    queryKey: ["/api/org/info"],
    retry: false,
  });

  const orgSlug = orgData?.slug || "org";

  const createDefaults: BookingPageFormValues = {
    organizationId: (user as any)?.organizationId || 0,
    slug: "",
    title: "",
    description: "",
    heroImage: "",
    isActive: true,
    primaryColor: "#C2652A",
    minStay: 1,
    maxStay: 365,
    advanceBookingDays: 365,
    termsAndConditions: "",
    cancellationPolicy: "",
  };

  async function onCreateSubmit(values: BookingPageFormValues) {
    setCreatePending(true);
    try {
      await apiRequest("POST", api.booking.pages.create.path, values);
      toast({ title: "Booking page created" });
      setCreateOpen(false);
      queryClient.invalidateQueries({ queryKey: [api.booking.pages.list.path] });
    } catch {
      toast({ title: "Error", description: "Failed to create booking page", variant: "destructive" });
    } finally {
      setCreatePending(false);
    }
  }

  async function onEditSubmit(values: BookingPageFormValues) {
    if (!editPage) return;
    setUpdatePending(true);
    try {
      await apiRequest("PUT", buildUrl(api.booking.pages.update.path, { id: editPage.id }), values);
      toast({ title: "Booking page updated" });
      setEditPage(null);
      queryClient.invalidateQueries({ queryKey: [api.booking.pages.list.path] });
    } catch {
      toast({ title: "Error", description: "Failed to update booking page", variant: "destructive" });
    } finally {
      setUpdatePending(false);
    }
  }

  async function handleDelete(id: number) {
    setDeletePending(true);
    try {
      await apiRequest("DELETE", buildUrl(api.booking.pages.delete.path, { id }));
      toast({ title: "Booking page deleted" });
      queryClient.invalidateQueries({ queryKey: [api.booking.pages.list.path] });
    } catch {
      toast({ title: "Error", description: "Failed to delete booking page", variant: "destructive" });
    } finally {
      setDeletePending(false);
    }
  }

  const getPublicUrl = (pageSlug: string) => {
    return `${window.location.origin}/book/${orgSlug}/${pageSlug}`;
  };

  return (
    <AdminLayout>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-display font-bold" data-testid="text-page-title">Booking Engine</h1>
          <p className="text-muted-foreground mt-2">Create and manage booking pages and rate plans for direct reservations.</p>
        </div>

        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button data-testid="button-add-booking-page">
              <Plus className="w-4 h-4 mr-2" /> Create Booking Page
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create Booking Page</DialogTitle>
            </DialogHeader>
            <BookingPageForm
              defaultValues={createDefaults}
              onSubmit={onCreateSubmit}
              isPending={createPending}
              submitLabel="Create Booking Page"
            />
          </DialogContent>
        </Dialog>
      </div>

      {isLoading && (
        <div className="text-center py-12 text-muted-foreground">Loading booking pages...</div>
      )}

      {bookingPages && bookingPages.length === 0 && !isLoading && (
        <div className="text-center py-12 text-muted-foreground">
          No booking pages yet. Create one to start accepting direct reservations.
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {bookingPages?.map((page) => (
          <Card key={page.id} data-testid={`card-booking-page-${page.id}`}>
            <CardContent className="p-6">
              <div className="flex justify-between items-start mb-4 gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-xl font-bold font-display" data-testid={`text-booking-page-title-${page.id}`}>{page.title}</h3>
                    <Badge variant={page.isActive ? "default" : "secondary"} data-testid={`badge-booking-page-status-${page.id}`}>
                      {page.isActive ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1">
                    <Globe className="w-3 h-3 flex-shrink-0" />
                    <span data-testid={`text-booking-page-slug-${page.id}`}>/{page.slug}</span>
                  </p>
                  {page.description && (
                    <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{page.description}</p>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => setEditPage(page)}
                    data-testid={`button-edit-booking-page-${page.id}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="icon" variant="ghost" data-testid={`button-delete-booking-page-${page.id}`}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Booking Page</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to delete "{page.title}"? This will also remove all associated rate plans.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel data-testid="button-cancel-delete">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleDelete(page.id)} data-testid="button-confirm-delete">
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>

              <div className="space-y-3">
                <div className="text-sm flex items-center gap-2 flex-wrap">
                  <span className="text-muted-foreground">Stay:</span>
                  <span>{page.minStay || 1}-{page.maxStay || 365} nights</span>
                </div>

                <div className="pt-3 border-t">
                  <p className="text-xs font-medium text-muted-foreground mb-1">PUBLIC URL</p>
                  <div className="flex gap-2">
                    <Input
                      readOnly
                      value={getPublicUrl(page.slug)}
                      className="text-xs font-mono bg-muted/50"
                      data-testid={`input-public-url-${page.id}`}
                    />
                    <Button
                      size="icon"
                      variant="outline"
                      data-testid={`button-copy-url-${page.id}`}
                      onClick={() => {
                        navigator.clipboard.writeText(getPublicUrl(page.slug));
                        toast({ title: "Copied to clipboard" });
                      }}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="outline"
                      data-testid={`button-open-url-${page.id}`}
                      onClick={() => window.open(getPublicUrl(page.slug), "_blank")}
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div className="pt-3">
                  <RatePlansDialog bookingPage={page} apartments={apartments || []} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={!!editPage} onOpenChange={(open) => { if (!open) setEditPage(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Booking Page</DialogTitle>
          </DialogHeader>
          {editPage && (
            <BookingPageForm
              defaultValues={{
                organizationId: editPage.organizationId,
                slug: editPage.slug,
                title: editPage.title,
                description: editPage.description || "",
                heroImage: editPage.heroImage || "",
                isActive: editPage.isActive,
                primaryColor: editPage.primaryColor || "#C2652A",
                minStay: editPage.minStay ?? 1,
                maxStay: editPage.maxStay ?? 365,
                advanceBookingDays: editPage.advanceBookingDays ?? 365,
                termsAndConditions: editPage.termsAndConditions || "",
                cancellationPolicy: editPage.cancellationPolicy || "",
              }}
              onSubmit={onEditSubmit}
              isPending={updatePending}
              submitLabel="Update Booking Page"
            />
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
